import { Link } from 'react-router-dom';
import { Shield, Hash, Eye, CheckCircle, ArrowRight } from 'lucide-react';
import styles from './ProvablyFair.module.css';

const steps = [
	{
		icon: Hash,
		title: 'Server Seed Commitment',
		description:
			'Before any bet, the server generates a seed and shares its hash with the player. This proves the outcome was determined before the bet.',
	},
	{
		icon: Shield,
		title: 'Client Seed Input',
		description:
			'Players can provide their own seed that influences the final outcome. This ensures the server cannot manipulate results.',
	},
	{
		icon: Eye,
		title: 'Transparent Verification',
		description:
			'After each game, players receive all data needed to verify the outcome was fair using HMAC-SHA256 cryptography.',
	},
];

const benefits = [
	'Mathematically verifiable randomness',
	'Zero trust required from players',
	'Regulatory compliance ready',
	'Build player confidence',
];

export default function ProvablyFair() {
	return (
		<section className={styles.provablyFair} id="provably-fair">
			<div className={styles.container}>
				<div className={styles.content}>
					<span className={styles.badge}>Provably Fair</span>
					<h2 className={styles.title}>
						Cryptographic Fairness Your Players Can Verify
					</h2>
					<p className={styles.subtitle}>
						Every game outcome is generated using HMAC-SHA256 cryptography. Players
						can independently verify that results were fair and not manipulated.
					</p>

					<div className={styles.steps}>
						{steps.map((step, index) => {
							const Icon = step.icon;
							return (
								<div key={step.title} className={styles.step}>
									<div className={styles.stepNumber}>{index + 1}</div>
									<div className={styles.stepContent}>
										<div className={styles.stepHeader}>
											<Icon size={20} className={styles.stepIcon} />
											<h3 className={styles.stepTitle}>{step.title}</h3>
										</div>
										<p className={styles.stepDescription}>{step.description}</p>
									</div>
								</div>
							);
						})}
					</div>

					<Link to="/docs/provably-fair" className={styles.link}>
						Learn how provably fair works
						<ArrowRight size={16} />
					</Link>
				</div>

				<div className={styles.visual}>
					<div className={styles.card}>
						<div className={styles.cardHeader}>
							<Shield size={24} className={styles.cardIcon} />
							<h3 className={styles.cardTitle}>Why Provably Fair?</h3>
						</div>
						<ul className={styles.benefitList}>
							{benefits.map((benefit) => (
								<li key={benefit} className={styles.benefitItem}>
									<CheckCircle size={18} className={styles.checkIcon} />
									<span>{benefit}</span>
								</li>
							))}
						</ul>
						<div className={styles.codePreview}>
							<div className={styles.codeLabel}>Verification Hash</div>
							<code className={styles.hash}>
								HMAC-SHA256(server_seed, client_seed:nonce)
							</code>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
